<template>
  <PageTemplate :max-width="maxWidth" :padding="padding">
    <!-- ページヘッダー -->
    <template #header>
      <PageHeader :title-size="titleSize" :alignment="titleAlignment">
        {{ title }}
        <template #subtitle>
          <slot name="subtitle" />
        </template>
        <template #actions>
          <slot name="actions" />
        </template>
      </PageHeader>
    </template>

    <!-- メインコンテンツ -->
    <template #content>
      <div class="settings-container">
        <!-- タブナビゲーション -->
        <AppTabs
          v-model="currentTab"
          :tabs="tabs"
          class="settings-tabs"
        />

        <!-- タブコンテンツ -->
        <TabContentArea
          :active-tab="currentTab"
          class="settings-content"
        >
          <!-- 各スロットを明示的に転送 -->
          <template #info_settings>
            <slot name="info_settings" />
          </template>
          <template #account_settings>
            <slot name="account_settings" />
          </template>
        </TabContentArea>
      </div>
    </template>
  </PageTemplate>
</template>

<script setup>
import { computed } from 'vue'
import PageTemplate from './PageTemplate.vue'
import PageHeader from '../molecules/PageHeader.vue'
import AppTabs from '../organisms/AppTabs.vue'
import TabContentArea from '../organisms/TabContentArea.vue'

/**
 * SettingsPageTemplate - 設定画面用テンプレートコンポーネント (Template)
 *
 * 設定画面系のページで使用される専用テンプレートです。
 * タブナビゲーション機能を持つページのレイアウト構造を定義します。
 *
 * アトミックデザインのTemplates層として、以下の責務を持ちます：
 * - タブ付き設定画面のレイアウト構造の定義
 * - タブナビゲーションとコンテンツエリアの配置管理
 * - 設定画面共通のスタイリング適用
 * - タブ切り替えロジックの抽象化
 *
 * 使用例:
 * <SettingsPageTemplate
 *   title="設定画面タイトル"
 *   :tabs="tabs"
 *   :active-tab="activeTab"
 *   @tab-change="handleTabChange"
 * >
 *   <!-- タブコンテンツをスロットで配置 -->
 * </SettingsPageTemplate>
 */

const props = defineProps({
  /**
   * ページタイトル
   */
  title: {
    type: String,
    required: true
  },
  /**
   * タイトルのサイズ
   */
  titleSize: {
    type: String,
    default: 'lg'
  },
  /**
   * タイトルの配置
   */
  titleAlignment: {
    type: String,
    default: 'left'
  },
  /**
   * タブの配列
   */
  tabs: {
    type: Array,
    required: true,
    validator: (tabs) => {
      return tabs.every(tab =>
        tab &&
        typeof tab.key === 'string' &&
        typeof tab.title === 'string'
      )
    }
  },
  /**
   * 現在アクティブなタブのキー
   */
  activeTab: {
    type: String,
    required: true
  },
  /**
   * ページの最大幅
   */
  maxWidth: {
    type: String,
    default: 'lg'
  },
  /**
   * ページのパディング
   */
  padding: {
    type: String,
    default: 'md'
  }
})

const emit = defineEmits(['tab-change'])

const currentTab = computed({
  get: () => props.activeTab,
  set: (value) => {
    handleTabChange(value)
  }
})

/**
 * タブ変更時のイベントハンドリング
 * @param {string} tabKey - 選択されたタブのキー
 */
const handleTabChange = (tabKey) => {
  /**
   * @event tab-change
   * @type {string} 選択されたタブのキー
   */
  emit('tab-change', tabKey)
}
</script>

<style scoped>
.settings-container {
  width: 100%;
}

.settings-tabs {
  margin-bottom: 1.5rem;
}

.settings-content {
  min-height: 400px;
}

/* レスポンシブ対応 */
@media (max-width: 768px) {
  .settings-tabs {
    margin-bottom: 1rem;
  }

  .settings-content {
    min-height: 300px;
  }
}
</style>
